import { useTheme } from '@emotion/react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { StageV2 } from 'modules/championship/domain/interfaces/stage';
import UDTippy from 'modules/ud-ui/components/tippy';
import React from 'react';
import { UDText } from 'ud-ui/components';
import * as S from './styles';

interface StageMatrixProps {
  stage: StageV2;
}

const StageMatrix: React.FC<StageMatrixProps> = ({ stage }) => {
  const { teams, matches } = stage;
  const theme = useTheme();

  const findMatches = (teamAId: number, teamBId: number) => {
    return matches.filter((match) => {
      const teamIds = match.teams.map((t) => t?.id);
      return teamIds.includes(teamAId) && teamIds.includes(teamBId);
    });
  };

  const renderMatchInfo = (match: any, teamAId: number, teamBId: number, showDate: boolean) => {
    if (!match.hasResults) {
      return (
        <S.MatchInfoWrapper>
          <S.MatchDate variant={'body-1'}>{format(new Date(match.date), 'MMM d', { locale: ru })}</S.MatchDate>
        </S.MatchInfoWrapper>
      );
    }

    const teamAIndex = match.teams.findIndex((t: any) => t?.id === teamAId);
    const teamBIndex = match.teams.findIndex((t: any) => t?.id === teamBId);

    const scoreA = match.teams[teamAIndex]?.score ?? 0;
    const scoreB = match.teams[teamBIndex]?.score ?? 0;

    let bgColor = '';
    if (scoreA > scoreB) {
      bgColor = theme.colors.submit; // Greenish for win
    } else if (scoreA < scoreB) {
      bgColor = theme.colors.error; // Reddish for loss
    } else if (scoreA === scoreB) {
      bgColor = theme.colors.AccentNaplesYellow;
    }

    return (
      <UDTippy content={format(new Date(match.date), 'MMM d', { locale: ru })} disabled={showDate}>
        <S.MatchInfoWrapper bgColor={bgColor}>
          <S.MatchResult variant={'body-1'}>{`${scoreA} - ${scoreB}`}</S.MatchResult>
          {showDate && <S.MatchDate variant={'body-1'}>{format(new Date(match.date), 'MMM d', { locale: ru })}</S.MatchDate>}
        </S.MatchInfoWrapper>
      </UDTippy>
    );
  };

  const getMatchContent = (teamAId: number, teamBId: number) => {
    if (teamAId === teamBId) {
      return <S.Cell isSelf={true} />;
    }

    const foundMatches = findMatches(teamAId, teamBId);
    const isRoundRobin2 = stage.type === 'ROUND_ROBIN_2';

    if (foundMatches.length === 0) {
      return (
        <S.Cell>
          <UDText variant={'body-1'}>---</UDText>
        </S.Cell>
      );
    }

    if (isRoundRobin2) {
      return (
        <S.Cell isSplit={true}>
          <S.SplitHalf>
            {foundMatches[0] ? (
              renderMatchInfo(foundMatches[0], teamAId, teamBId, false)
            ) : (
              <S.MatchInfoWrapper>
                <UDText variant={'body-1'}>---</UDText>
              </S.MatchInfoWrapper>
            )}
          </S.SplitHalf>
          <S.SplitHalf>
            {foundMatches[1] ? (
              renderMatchInfo(foundMatches[1], teamAId, teamBId, false)
            ) : (
              <S.MatchInfoWrapper>
                <UDText variant={'body-1'}>---</UDText>
              </S.MatchInfoWrapper>
            )}
          </S.SplitHalf>
        </S.Cell>
      );
    }

    return <S.Cell>{renderMatchInfo(foundMatches[0], teamAId, teamBId, true)}</S.Cell>;
  };

  return (
    <S.MatrixContainer>
      <S.MatrixTable size={teams.length}>
        {/* Rows for each team */}
        {teams.map((teamA) => (
          <React.Fragment key={`row-${teamA.id}`}>
            {/* Left team logo */}
            <UDTippy content={teamA.name}>
              <S.TeamLogoCell>{teamA.logo?.url && <img src={teamA.logo.url} alt={teamA.name} />}</S.TeamLogoCell>
            </UDTippy>

            {/* Match cells */}
            {teams.map((teamB) => (
              <React.Fragment key={`cell-${teamA.id}-${teamB.id}`}>{getMatchContent(teamA.id, teamB.id)}</React.Fragment>
            ))}
          </React.Fragment>
        ))}

        {/* Bottom row for team logos */}
        <S.EmptyCell />
        {teams.map((team) => (
          <UDTippy key={`bottom-${team.id}`} content={team.name}>
            <S.TeamLogoCell>{team.logo?.url && <img src={team.logo.url} alt={team.name} />}</S.TeamLogoCell>
          </UDTippy>
        ))}
      </S.MatrixTable>
    </S.MatrixContainer>
  );
};

export default StageMatrix;
